import React from "react";
import LegalDocument from "./LegalDocument";

export default function Terms() {
    return (
        <LegalDocument
            activePage="Terms"
            title="42 Degrees Terms of Service"
            effectiveDate="April 13, 2026"
            lastUpdated="April 13, 2026"
        >
            <h2>1. Introduction</h2>
            <p>
                These Terms of Service ("Terms") govern your access to and use of the 42 Degrees research novelty
                prediction scoring platform ("Platform" or "Service") operated by 42 Degrees Corp., a Delaware corporation
                with its principal place of business at 123 Front Street, San Diego, California 92115 ("42 Degrees," "we,"
                "us," or "our"), accessible at https://fortytwodegrees.org.
            </p>
            <p>
                By accessing or using the Service, you ("User," "you," or "your") agree to be bound by these Terms. If you
                do not agree to these Terms, you must not use the Service.
            </p>

            <h2>2. Definitions</h2>
            <p><strong>"Manuscript"</strong> means any research paper, article, or academic document you submit to the Platform for analysis.</p>
            <p>
                <strong>"Novelty Score"</strong> means the algorithmic output generated by the Platform that predicts the
                research novelty of a submitted Manuscript. "Methodology Score" and "Impact Score" refer to the
                corresponding outputs for those dimensions.
            </p>
            <p><strong>"User Content"</strong> means all Manuscripts and any other materials, data, or information you submit to or through the Service.</p>
            <p>
                <strong>"Output"</strong> means the Novelty, Methodology, and Impact Scores, written analyses, similar
                paper lists, network graphs, PDF reports, and any other analysis generated by the Platform based on your
                User Content.
            </p>
            <p><strong>"Peer Review"</strong> means the process by which a Manuscript is evaluated by an assigned Reviewer through the Platform.</p>
            <p><strong>"Reviewer"</strong> means a person selected from 42 Degrees' curated reviewer whitelist to evaluate a Manuscript submitted for Peer Review.</p>
            <p><strong>"Review Feedback"</strong> means the scores, written assessment, and recommendation submitted by a Reviewer as part of a Peer Review.</p>
            <p><strong>"Showcase Paper"</strong> means a Manuscript designated by administrators as publicly accessible via a unique shareable link without requiring authentication.</p>
            <p><strong>"Featured Manuscript" / "Paper of the Week"</strong> means a Manuscript belonging to a user who has opted in to the featured-manuscript program ("Featured Eligible") and which the Platform has selected to display on other users' dashboards for a limited time.</p>
            <p><strong>"Community Feed"</strong> means the in-Platform activity stream that displays achievement events (e.g., high scores, milestones, featured-this-week designations) from users who have opted in to feed sharing.</p>
            <p><strong>"Public Profile"</strong> means a user profile page that has been opted in by its owner to be visible without authentication.</p>

            <h2>3. Eligibility and Account Registration</h2>

            <h3>3.1 Age Requirement</h3>
            <p>
                You must be at least 18 years of age to use the Service. By using the Service, you represent and warrant
                that you are at least 18 years old.
            </p>

            <h3>3.2 Account Registration</h3>
            <p>To access the features of the Service, you must register for an account. You agree to:</p>
            <ul>
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and accept all risks of unauthorized access</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3>3.3 Account Responsibility</h3>
            <p>
                You are responsible for all activities that occur under your account, regardless of whether the activities
                are authorized by you or undertaken by you, your employees, or third parties.
            </p>

            <h2>4. Service Offerings</h2>

            <h3>4.1 Current Service Model</h3>
            <p>
                The Service is currently offered free of charge during our initial release period. We do not currently
                process payments or offer paid subscription tiers. We reserve the right to introduce paid tiers, usage
                limits, or other commercial models in the future with at least thirty (30) days' advance notice to existing
                users.
            </p>

            <h3>4.2 Future Pricing Changes</h3>
            <p>
                If we introduce paid features in the future, we will provide clear notice and you will have the option to
                continue using any free tier offered or terminate your account before any charges are incurred.
            </p>

            <h3>4.3 API Access</h3>
            <p>
                We do not currently offer public API access. If we offer API access in the future, it will be governed by
                a separate API Agreement.
            </p>

            <h2>5. Use of the Service</h2>

            <h3>5.1 License Grant</h3>
            <p>
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable,
                revocable license to access and use the Service for your personal or internal academic research purposes.
            </p>

            <h3>5.2 Acceptable Use</h3>
            <p>You agree not to:</p>
            <ul>
                <li>Use the Service for any illegal, fraudulent, or unauthorized purpose</li>
                <li>Violate any applicable local, state, national, or international law</li>
                <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
                <li>Attempt to gain unauthorized access to any portion of the Service</li>
                <li>Use any automated means (scraping, bots, scripts) to access the Service except as expressly permitted in writing</li>
                <li>Reverse engineer, decompile, or attempt to discover the source code or underlying algorithms of the Service</li>
                <li>Remove, circumvent, disable, damage, or otherwise interfere with security-related features of the Service</li>
                <li>Upload or transmit viruses, malware, or any code of a destructive nature</li>
                <li>Harvest or collect information about users without their consent</li>
                <li>Impersonate any person or entity</li>
                <li>Submit Manuscripts that you do not have the legal right to submit or that infringe on third-party intellectual property rights</li>
                <li>Submit Manuscripts containing classified information, export-controlled data, or information subject to ITAR restrictions</li>
                <li>Use the Service in a manner that exceeds reasonable usage limits as determined by us in our discretion</li>
            </ul>

            <h3>5.3 Peer Review Conduct</h3>
            <p><strong>If you submit a Manuscript for Peer Review, you agree that:</strong></p>
            <ul>
                <li>Your manuscript title, authors, any notes you provide, and the manuscript PDF (if stored) will be shared with the assigned Reviewer</li>
                <li>You may cancel a pending or in-progress Peer Review at any time, which removes it from the Reviewer's queue and deletes the associated review data</li>
                <li>Review Feedback represents the Reviewer's independent opinion and is subject to the same disclaimers as Output in Section 8</li>
                <li>You will not use Review Feedback as the sole basis for publication decisions, funding applications, or any other critical determinations</li>
                <li>A Manuscript with an active (non-completed) Peer Review cannot be submitted for another Peer Review until the current one is completed or cancelled</li>
            </ul>
            <p><strong>If you are invited to serve as a Reviewer, you agree that:</strong></p>
            <ul>
                <li>You will provide honest, constructive, and professional feedback</li>
                <li>You will complete reviews within the assigned deadline (currently 14 days from acceptance)</li>
                <li>You will not share, redistribute, or retain copies of the Manuscript content outside the Platform</li>
                <li>You may decline or cancel a review assignment at any time</li>
                <li>Your name, institution, and specialty will be visible to the Manuscript submitter</li>
                <li>Your Review Feedback (scores, written assessment, and recommendation) will be shared with the Manuscript submitter</li>
                <li>Repeated failure to complete accepted reviews or violations of reviewer conduct may result in removal from the reviewer whitelist</li>
            </ul>

            <h3>5.4 Community Feed, Featured Manuscripts, and Public Profile</h3>
            <p>
                The Platform offers optional sharing features that are <strong>off by default</strong>. You may enable any
                combination of them in Settings, and you may disable any of them at any time. By enabling these features,
                you acknowledge that:
            </p>
            <ul>
                <li>
                    <strong>Community Feed:</strong> If you enable feed sharing, your display name, profile image,
                    manuscript title, achievement event type, and the associated score or count value become visible to
                    other authenticated users on the Platform. Disabling stops new events from being generated; existing
                    feed events tied to your account are removed when you delete the associated manuscript or your account.
                </li>
                <li>
                    <strong>Featured Manuscript / Paper of the Week:</strong> If you enable "Featured Eligible," 42 Degrees
                    may select one of your manuscripts to display on other users' dashboards. You separately control
                    whether your name is shown and whether the PDF is downloadable; both are off by default.
                </li>
                <li>
                    <strong>Public Profile:</strong> If you enable "Profile Public," your profile page (display name,
                    profile image, account creation date, and any submissions you have elected to make public) becomes
                    accessible without authentication at a stable URL.
                </li>
            </ul>
            <p>
                Other users may take screenshots, copy text, or otherwise record content that becomes publicly visible
                through these features. You should not enable these features for any manuscript or profile information
                that you wish to remain confidential.
            </p>

            <h2>6. User Content and Intellectual Property</h2>

            <h3>6.1 Ownership of User Content</h3>
            <p>
                You retain all ownership rights in and to your User Content, including all Manuscripts you submit to the
                Platform. By submitting User Content, you represent and warrant that:
            </p>
            <ul>
                <li>You own or have obtained all necessary rights, licenses, consents, and permissions to submit the User Content</li>
                <li>Your User Content does not infringe, violate, or misappropriate any third-party intellectual property or proprietary rights</li>
                <li>You have the authority to grant the licenses described in these Terms</li>
            </ul>

            <h3>6.2 License to Use User Content</h3>
            <p>
                By submitting User Content to the Service, you grant us a limited, non-exclusive, royalty-free, worldwide
                license to:
            </p>
            <ul>
                <li>Store, process, and transmit your User Content for the purpose of providing the Service to you</li>
                <li>Generate Output based on your User Content and persist that Output in your account</li>
                <li>Transmit the textual content of your Manuscript to our third-party AI sub-processor (currently DeepSeek) for the sole purpose of generating Output</li>
                <li>Share your Manuscript title, authors, notes, and PDF with an assigned Reviewer if you submit the Manuscript for Peer Review</li>
                <li>Perform technical operations necessary to operate, secure, and maintain the Service</li>
            </ul>
            <p>
                This license terminates when you delete your User Content from the Service, except to the extent we are
                required to retain copies for legal, compliance, or backup purposes.
            </p>

            <h3>6.3 No Use for Algorithm Training</h3>
            <p>
                We do not use your User Content to train, improve, or develop our algorithms or any machine learning
                models. We instruct our AI sub-processors not to use your content for training, but you should be aware
                that your manuscript text is transmitted to a third-party AI provider as described in Section 6.2 and in
                our Privacy Policy.
            </p>

            <h3>6.4 Ownership of Output</h3>
            <p>
                You own all Output generated by the Platform based on your User Content. We grant you full rights to use,
                reproduce, distribute, and commercialize the Output as you see fit.
            </p>

            <h3>6.5 Ownership of Review Feedback</h3>
            <p>
                Review Feedback submitted by a Reviewer becomes part of the peer review record associated with your
                Manuscript. Both the submitter and the Reviewer retain the right to reference or use the Review Feedback
                for their own purposes. 42 Degrees does not claim ownership of Review Feedback.
            </p>

            <h3>6.6 42 Degrees Intellectual Property</h3>
            <p>
                The Service, including all software, algorithms, scoring methodologies, user interfaces, visual designs,
                trademarks, logos, and other intellectual property (excluding User Content and Output) is owned by 42
                Degrees and is protected by United States and international intellectual property laws.
            </p>

            <h3>6.7 Feedback</h3>
            <p>
                If you provide us with any suggestions, ideas, enhancement requests, or other feedback regarding the
                Service ("Feedback"), you grant us an unrestricted, perpetual, irrevocable, non-exclusive, royalty-free,
                worldwide right and license to use, reproduce, disclose, sublicense, distribute, modify, and otherwise
                exploit such Feedback without compensation or attribution.
            </p>

            <h2>7. Data Handling and Storage</h2>

            <h3>7.1 Manuscript File Handling</h3>
            <p>
                When you upload a Manuscript, the PDF is uploaded to our cloud storage so that our processing service can
                read it. <strong>By default, the PDF is automatically deleted immediately after processing completes.</strong>{" "}
                Your account Settings include a "Store Manuscripts" preference that is <strong>off by default</strong>. If
                you turn it on, your PDF will be retained after processing so that you can download it again later. You
                can turn this setting on or off at any time.
            </p>
            <p>Stored manuscripts are required for peer review eligibility. Only manuscripts that remain in cloud storage can be submitted for peer review.</p>

            <h3>7.2 Output Storage</h3>
            <p>
                Output generated from your Manuscript (manuscript metadata, Novelty/Methodology/Impact scores, written
                analyses, similar paper lists, the interactive network graph, and the Deep Thinkers content file) is
                stored on our infrastructure for the lifetime of the associated Manuscript. This Output is deleted when
                you delete the Manuscript or your account.
            </p>

            <h3>7.3 Reports</h3>
            <p>
                Generated PDF reports are not stored after delivery. A record of each generated report (report ID,
                manuscript ID, creation date, and recipient email if shared) is stored to support the report verification
                feature.
            </p>
            <p>Refer to our Privacy Policy for full details on data retention, deletion, and your rights.</p>

            <h3>7.4 Data Deletion</h3>
            <p>
                You may delete individual Manuscripts at any time using the in-app delete action, or delete your entire
                account from Settings. Deletion removes the Manuscript record, all associated analyses, similar paper
                records, network graph file, Deep Thinkers content file, all peer review records, all reviewer invitations,
                all review feedback, the PDF (if stored), and your activity log entries.
            </p>

            <h3>7.5 Third-Party Sub-Processors</h3>
            <p>
                We use third-party sub-processors to operate the Service. The current list (which may change with notice)
                includes Amazon Web Services (cloud infrastructure — RDS database, S3 file storage), DeepSeek (AI
                inference), Microsoft Clarity (behavioral analytics), Google Sign-In (optional authentication), Google
                Sheets / Google Workspace (feedback form storage), and Google Gmail SMTP (transactional email delivery
                from deepthought@fortytwodegrees.org). See our Privacy Policy Section 5.2 for full details.
            </p>

            <h3>7.6 Security Measures</h3>
            <ul>
                <li>HTTPS/TLS for data in transit</li>
                <li>Provider-managed encryption at rest for our database and file storage</li>
                <li>One-way hashing with per-password salt for user passwords</li>
                <li>JWT-based session management with refresh-token rotation</li>
                <li>Role-based access control (USER vs. ADMIN) at the API layer</li>
                <li>Reviewer access determined by curated whitelist membership</li>
            </ul>

            <h3>7.7 Showcase Papers</h3>
            <p>
                Administrators may designate certain Manuscripts as Showcase Papers that are publicly accessible via unique
                shareable links without requiring authentication. Showcase Papers display the full report including scores,
                written analyses, similar papers, network graph, Deep Thinkers data, and a downloadable PDF export to
                anyone with the link. Administrators may remove Showcase status or delete Showcase Papers at any time,
                which immediately revokes public access.
            </p>

            <h2>8. Intended Use and Disclaimers</h2>

            <h3>8.1 Intended Purpose</h3>
            <p>
                The Service is designed to provide users with an analytical tool to assess research novelty, methodology,
                and impact. The Output is intended to:
            </p>
            <ul>
                <li>Offer one perspective on research quality and novelty</li>
                <li>Serve as one data point among many in evaluating research</li>
                <li>Assist in early-stage research assessment and planning</li>
            </ul>

            <h3>8.2 User Discretion and Interpretation</h3>
            <p><strong>IMPORTANT:</strong> You acknowledge and agree that:</p>
            <ul>
                <li>The interpretation and use of Output is entirely at your discretion</li>
                <li>You bear full and sole responsibility for any decisions or actions taken based on Output</li>
                <li>Output should not be the sole basis for publication decisions, funding applications, or any other critical determinations</li>
                <li>You should exercise independent professional judgment when using Output</li>
                <li>Review Feedback provided by Reviewers represents independent opinions and is subject to the same disclaimers as algorithmic Output</li>
            </ul>

            <h3>8.3 No Guarantees of Accuracy</h3>
            <p>
                While we strive to provide accurate and reliable Output, we make no warranties or guarantees regarding the
                accuracy, completeness, or reliability of Output, the suitability of Output for any particular purpose, the
                quality or accuracy of Review Feedback, or the results you may achieve.
            </p>

            <h3>8.4 Not Professional Advice</h3>
            <p>
                The Service does not provide professional, scientific, medical, legal, or financial advice. The Output and
                Review Feedback should not be relied upon as a substitute for consultation with qualified professionals in
                relevant fields.
            </p>

            <h2>9. Copyright Policy (DMCA)</h2>

            <h3>9.1 Notice of Infringement</h3>
            <p>
                We respect the intellectual property rights of others and expect users to do the same. If you believe that
                any content available through the Service infringes your copyright, you may submit a notice pursuant to
                the Digital Millennium Copyright Act ("DMCA") by providing our Designated Copyright Agent with the
                following information in writing:
            </p>
            <ul>
                <li>A physical or electronic signature of the copyright owner or a person authorized to act on their behalf</li>
                <li>Identification of the copyrighted work claimed to have been infringed</li>
                <li>Identification of the material that is claimed to be infringing and information reasonably sufficient to permit us to locate it</li>
                <li>Your contact information, including address, telephone number, and email address</li>
                <li>A statement that you have a good faith belief that use of the material is not authorized by the copyright owner</li>
                <li>A statement, made under penalty of perjury, that the information in the notification is accurate and that you are authorized to act on behalf of the copyright owner</li>
            </ul>

            <h3>9.2 Designated Copyright Agent</h3>
            <p className="legal-doc-address">
                <strong>42 Degrees Corp. — Designated Copyright Agent</strong><br />
                123 Front Street<br />
                San Diego, California 92115<br />
                Email: dmca@fortytwodegrees.org
            </p>

            <h3>9.3 Counter-Notification</h3>
            <p>
                If you believe that your content was removed or disabled by mistake or misidentification, you may submit a
                counter-notification containing the elements required by 17 U.S.C. Section 512(g)(3) to our Designated
                Copyright Agent.
            </p>

            <h3>9.4 Repeat Infringer Policy</h3>
            <p>We will terminate the accounts of users who are determined to be repeat infringers.</p>

            <h2>10. Term and Termination</h2>

            <h3>10.1 Term</h3>
            <p>
                These Terms commence when you first access or use the Service and continue until terminated in accordance
                with this Section.
            </p>

            <h3>10.2 Termination by You</h3>
            <p>You may terminate your account at any time by:</p>
            <ul>
                <li>Using the "Delete Account" feature in Settings</li>
                <li>Sending a termination notice to support@fortytwodegrees.org</li>
                <li>Ceasing all use of the Service</li>
            </ul>

            <h3>10.3 Termination by Us</h3>
            <p>
                We may suspend or terminate your access to the Service for any reason, including breach of these Terms,
                fraudulent or abusive activity, or at our discretion. For non-emergency terminations, we will use
                commercially reasonable efforts to provide advance notice. For emergency terminations (e.g., security
                threats, ToS violations), we may act immediately.
            </p>

            <h3>10.4 Effect of Termination</h3>
            <p>
                Upon termination, your right to access and use the Service immediately ceases. We will delete your User
                Content in accordance with our data retention policies. Any active Peer Reviews associated with your
                account will be cancelled and removed from reviewers' queues. Sections that by their nature should survive
                termination will survive, including intellectual property provisions, disclaimers, limitations of liability,
                and dispute resolution provisions.
            </p>

            <h2>11. Warranties and Disclaimers</h2>

            <h3>11.1 Service Provided "As-Is"</h3>
            <p>
                THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER
                EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
            </p>

            <h3>11.2 No Warranty of Availability</h3>
            <p>42 DEGREES DOES NOT WARRANT THAT:</p>
            <ul>
                <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                <li>The results obtained from the use of the Service will be accurate or reliable</li>
                <li>The quality of any Output or Review Feedback will meet your expectations</li>
                <li>Any errors in the Service will be corrected</li>
                <li>Peer Review assignments will be completed within any specific timeframe</li>
            </ul>

            <h3>11.3 Third-Party Services</h3>
            <p>
                The Service relies on third-party infrastructure and services, including AWS, DeepSeek, Microsoft Clarity,
                and Google. We are not responsible for any failures, interruptions, or issues arising from these third-party
                services.
            </p>

            <h3>11.4 Your Warranties</h3>
            <p>You represent and warrant that:</p>
            <ul>
                <li>You have the legal capacity to enter into these Terms</li>
                <li>All information you provide is accurate and current</li>
                <li>Your use of the Service complies with all applicable laws and regulations</li>
                <li>You have all necessary rights to submit your User Content</li>
            </ul>

            <h2>12. Limitation of Liability</h2>

            <h3>12.1 Maximum Liability</h3>
            <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL 42 DEGREES, ITS OFFICERS, DIRECTORS,
                EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE TO YOU FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE,
                CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, REVENUE, BUSINESS
                OPPORTUNITIES, DATA, REPUTATION, OR ANY OTHER INTANGIBLE LOSSES ARISING FROM OR RELATING TO THESE TERMS OR
                YOUR USE OF THE SERVICE, REGARDLESS OF THE THEORY OF LIABILITY.
            </p>

            <h3>12.2 Liability Cap</h3>
            <p>
                TO THE EXTENT PERMITTED BY APPLICABLE LAW, 42 DEGREES' TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED
                TO THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED ONE HUNDRED DOLLARS ($100.00).
            </p>

            <h3>12.3 User Responsibility</h3>
            <p>
                YOU ACKNOWLEDGE AND AGREE THAT YOUR USE OF OUTPUT AND REVIEW FEEDBACK IS ENTIRELY AT YOUR OWN RISK AND
                DISCRETION. YOU ASSUME FULL AND SOLE LIABILITY FOR ANY DECISIONS, ACTIONS, OR CONSEQUENCES RESULTING FROM
                YOUR USE OF THE SERVICE OR RELIANCE ON OUTPUT OR REVIEW FEEDBACK.
            </p>

            <h2>13. Indemnification</h2>
            <p>
                You agree to indemnify, defend, and hold harmless 42 Degrees and its officers, directors, employees,
                agents, and affiliates from and against any and all losses, damages, liabilities, claims, actions,
                judgments, costs, or expenses (including reasonable attorneys' fees) arising from or relating to your use
                or misuse of the Service, your breach of these Terms, your violation of any law or third-party right, your
                User Content, your Review Feedback (if you serve as a Reviewer), or any negligence or willful misconduct
                by you.
            </p>

            <h2>14. Confidentiality</h2>
            <p>
                Each party agrees to maintain the confidentiality of non-public information disclosed by the other party
                in connection with the Service. We treat your User Content as Confidential Information and use it only for
                the purposes described in these Terms and our Privacy Policy. Reviewers are required to maintain the
                confidentiality of Manuscripts they review and may not share, redistribute, or retain manuscript content
                outside the Platform.
            </p>

            <h2>15. Dispute Resolution</h2>

            <h3>15.1 Governing Law</h3>
            <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of California,
                United States, without regard to its conflict of law provisions.
            </p>

            <h3>15.2 Informal Resolution</h3>
            <p>
                Before filing any formal legal action, you agree to attempt to resolve any dispute informally by contacting
                us at legal@fortytwodegrees.org. We will attempt to resolve the dispute informally within thirty (30) days.
            </p>

            <h3>15.3 Arbitration Agreement</h3>
            <p>
                <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A
                LAWSUIT IN COURT.</strong>
            </p>
            <p>
                If informal resolution fails, you agree that any dispute, controversy, or claim arising out of or relating
                to these Terms or the Service shall be resolved through binding arbitration administered by the American
                Arbitration Association (AAA) under its Commercial Arbitration Rules. The arbitration shall be conducted
                in San Diego, California by a single arbitrator. The arbitrator's decision will be final and binding.
            </p>

            <h3>15.4 Arbitration Opt-Out</h3>
            <p>
                You may opt out of the arbitration agreement in Section 15.3 by sending written notice of your decision to
                opt out to legal@fortytwodegrees.org within thirty (30) days of first accepting these Terms. Your notice
                must include your name, account email, and a clear statement that you wish to opt out of arbitration.
                Opting out will not affect any other provision of these Terms.
            </p>

            <h3>15.5 Class Action Waiver</h3>
            <p>
                YOU AND 42 DEGREES AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL
                CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
            </p>

            <h3>15.6 Exceptions</h3>
            <p>
                Either party may seek injunctive or other equitable relief in any court of competent jurisdiction to
                prevent the infringement of intellectual property rights or confidential information.
            </p>

            <h2>16. Modifications to Terms</h2>
            <p>
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting
                the updated Terms on the Platform with a new "Effective Date," sending notice to your account email,
                and/or providing notice through the Service interface. For material changes that affect your rights or
                obligations, we will provide at least thirty (30) days' notice before the changes take effect.
            </p>

            <h2>17. General Provisions</h2>

            <h3>17.1 Entire Agreement</h3>
            <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and 42 Degrees
                regarding the Service and supersede all prior or contemporaneous communications and proposals.
            </p>

            <h3>17.2 Severability</h3>
            <p>
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions
                will continue in full force and effect.
            </p>

            <h3>17.3 Waiver</h3>
            <p>
                Our failure to assert any right or provision under these Terms shall not constitute a waiver of such right
                or provision.
            </p>

            <h3>17.4 Assignment</h3>
            <p>
                You may not assign these Terms without our prior written consent. We may assign these Terms at any time
                without notice or consent.
            </p>

            <h3>17.5 Force Majeure</h3>
            <p>
                Neither party shall be liable for any failure or delay in performance due to causes beyond its reasonable
                control, including acts of God, natural disasters, war, terrorism, pandemics, or infrastructure failures.
            </p>

            <h3>17.6 Export Controls</h3>
            <p>
                You agree to comply with all applicable export and import control laws. You represent that you are not
                located in, under the control of, or a national or resident of any country to which the United States has
                embargoed goods or services.
            </p>

            <h2>18. Contact Information</h2>
            <p>For questions about these Terms or the Service, please contact us at:</p>
            <p className="legal-doc-address">
                <strong>42 Degrees Corp.</strong><br />
                123 Front Street<br />
                San Diego, California 92115<br />
                <br />
                Email: support@fortytwodegrees.org<br />
                Legal: legal@fortytwodegrees.org<br />
                DMCA: dmca@fortytwodegrees.org<br />
                Privacy: privacy@fortytwodegrees.org<br />
                Website: https://fortytwodegrees.org
            </p>
            <p>
                <strong>BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS, UNDERSTAND THEM, AND AGREE
                TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT USE THE SERVICE.</strong>
            </p>
        </LegalDocument>
    );
}
